import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import BuildPhaseCell from './BuildPhaseCell';

const PHASE_BADGE = {
  queued:            'bg-[#F0EFF4]/10 text-[#F0EFF4]/50',
  setup:             'bg-yellow-500/20 text-yellow-400',
  customization:     'bg-[#7B61FF]/20 text-[#7B61FF]',
  awaiting_delivery: 'bg-blue-500/20 text-blue-400',
  delivery:          'bg-orange-500/20 text-orange-400',
  deployed:          'bg-emerald-500/20 text-emerald-400',
};

const PHASE_LABELS = {
  queued:            'Queued',
  setup:             'Setup',
  customization:     'Custom.',
  awaiting_delivery: 'Awaiting',
  delivery:          'Delivery',
  deployed:          'Deployed',
};

const TempBadge = ({ workarounds }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  if (!workarounds || workarounds.length === 0) return null;

  return (
    <div className="relative inline-flex">
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={e => { e.stopPropagation(); setShowTooltip(v => !v); }}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-data font-bold ring-1 ring-amber-500/30 hover:bg-amber-500/30 transition-colors"
      >
        <AlertTriangle className="w-3 h-3" />
        {workarounds.length} temp
      </button>
      {showTooltip && (
        <div className="absolute z-50 top-full left-0 mt-1 w-52 bg-[#1a1a2e] border border-amber-500/30 rounded-lg shadow-xl p-3 animate-fade-in">
          <div className="text-[11px] font-bold text-amber-400 mb-1.5">⚠️ Temporary Items</div>
          <div className="text-[11px] text-[#F0EFF4]/60 mb-2">Resolve before delivery:</div>
          {workarounds.map(w => (
            <div key={w.step_id} className="flex items-center gap-1.5 py-1 border-t border-[#F0EFF4]/5 first:border-0">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
              <span className="text-[11px] text-[#F0EFF4]/80">{w.label}</span>
            </div>
          ))}
          <div className="text-[10px] text-[#F0EFF4]/30 mt-2 pt-1.5 border-t border-[#F0EFF4]/5">
            These steps were marked N/A during build. Client needs permanent versions before handoff.
          </div>
        </div>
      )}
    </div>
  );
};

const BuildClientColumn = ({ build, onStartBuild }) => {
  const navigate = useNavigate();
  const { id, phase, client, hardware, progress, temp_workarounds } = build;
  const clientName = client?.name || 'Unknown';
  const agentName = client?.company || '—';
  const hwLabel = hardware?.label || hardware?.serial_number || '—';

  return (
    <div className="flex flex-col min-w-[180px] max-w-[220px] border-r border-[#F0EFF4]/10 last:border-r-0">
      {/* Header */}
      <div
        className="px-3 py-3 border-b border-[#F0EFF4]/10 cursor-pointer hover:bg-[#F0EFF4]/5 transition-colors"
        onClick={() => navigate(`/admin/build/${id}`)}
      >
        <div className="font-bold text-[#F0EFF4] text-sm truncate mb-0.5">{clientName}</div>
        <div className="text-[11px] text-[#F0EFF4]/50 font-data truncate mb-1.5">{agentName}</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-data font-bold ${PHASE_BADGE[phase] || PHASE_BADGE.queued}`}>
            {PHASE_LABELS[phase] || phase}
          </span>
          <TempBadge workarounds={temp_workarounds} />
          {hwLabel !== '—' && (
            <span className="text-[10px] font-data text-[#F0EFF4]/30 truncate">{hwLabel}</span>
          )}
        </div>
        {phase === 'queued' && (
          <button
            onClick={e => { e.stopPropagation(); onStartBuild(id); }}
            className="mt-2 w-full text-[11px] font-data font-bold px-2 py-1 bg-[#7B61FF]/20 hover:bg-[#7B61FF]/40 text-[#7B61FF] rounded transition-colors"
          >
            + Start Build
          </button>
        )}
      </div>

      {/* Phase rows */}
      <div className="px-2 py-2 flex flex-col gap-1.5">
        <BuildPhaseCell buildId={id} phaseRow="setup"         progress={progress?.setup}         buildPhase={phase} />
        <BuildPhaseCell buildId={id} phaseRow="customization" progress={progress?.customization}  buildPhase={phase} />
        <BuildPhaseCell buildId={id} phaseRow="delivery"      progress={progress?.delivery}       buildPhase={phase} />
      </div>
    </div>
  );
};

export default BuildClientColumn;
